import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import GlobalFilter from "../../Utility/GlobalFilter/GlobalFilter";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import { selectTeamsData } from "../../Redux/Slices/teamsSlice";
import { getListFromApiResponse } from "../utils/apiListMappers";
import {
  mapTeamsToFilterOptions,
  ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS,
  DEFAULT_PRIORITY_FILTER_OPTIONS,
  DEFAULT_WORK_ITEM_TYPE_FILTER_OPTIONS,
} from "./issueMappers";
import { mapProjectsToFilterOptions } from "../Projects/projectMappers";
import { buildWorkItemsGlobalFilterFields } from "./issueListFilterFields";

export default function WorkItemsFilterBar({
  listingFilter,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  assigneeOptions = [],
  projectSlug = null,
  projectsData = null,
  clientsData = null,
  typeOptions = null,
  statusOptions: _statusOptionsProp = null,
  /** Filter dropdown values — coarse `statusCategories` (NEW, IN_PROGRESS, …). */
  statusFilterOptions: statusFilterOptionsProp = null,
  priorityOptions = null,
  filterMode = "all",
  /** When true, hides the Filter button — changes apply automatically via parent debounce. */
  liveMode = false,
}) {
  const dispatch = useDispatch();
  const teamsData = useSelector(selectTeamsData);
  const showAssigneeAndTeam = filterMode !== "my";

  useEffect(() => {
    if (!showAssigneeAndTeam) return;
    dispatch(fetchTeamsData({ getAll: true }));
  }, [dispatch, showAssigneeAndTeam]);

  const teamOptions = useMemo(() => {
    if (!showAssigneeAndTeam) return [];
    return mapTeamsToFilterOptions(teamsData);
  }, [teamsData, showAssigneeAndTeam]);

  const clientFilterOptions = useMemo(() => {
    if (projectSlug) return [];
    return getListFromApiResponse(clientsData)
      .map((client) => {
        const id = client?.id ?? client?.clientId;
        const name = client?.name ?? client?.clientName ?? "";
        if (!id || !name) return null;
        return { label: name, value: String(id) };
      })
      .filter(Boolean);
  }, [projectSlug, clientsData]);

  const projectFilterOptions = useMemo(() => {
    if (projectSlug) return [];
    return mapProjectsToFilterOptions(projectsData);
  }, [projectSlug, projectsData]);

  const typeFilterOptions = typeOptions?.length
    ? typeOptions
    : DEFAULT_WORK_ITEM_TYPE_FILTER_OPTIONS;
  const priorityFilterOptions = priorityOptions?.length
    ? priorityOptions
    : DEFAULT_PRIORITY_FILTER_OPTIONS;
  const statusOptionsForFilter = statusFilterOptionsProp?.length
    ? statusFilterOptionsProp
    : ALL_ISSUES_STATUS_CATEGORY_FILTER_OPTIONS;

  const assigneeOptionsWithAvatar = useMemo(
    () =>
      assigneeOptions.map((opt) =>
        typeof opt === "string"
          ? { label: opt, value: opt, showAvatar: true }
          : { ...opt, showAvatar: true }
      ),
    [assigneeOptions]
  );

  const reporterOptionsWithAvatar = assigneeOptionsWithAvatar;

  const fields = useMemo(
    () =>
      buildWorkItemsGlobalFilterFields({
        projectSlug,
        clientFilterOptions,
        projectFilterOptions,
        typeFilterOptions,
        statusOptionsForFilter,
        priorityFilterOptions,
        assigneeOptionsWithAvatar,
        reporterOptionsWithAvatar,
        teamOptions,
        showAssigneeAndTeam,
      }),
    [
      projectSlug,
      clientFilterOptions,
      projectFilterOptions,
      typeFilterOptions,
      statusOptionsForFilter,
      priorityFilterOptions,
      assigneeOptionsWithAvatar,
      reporterOptionsWithAvatar,
      teamOptions,
      showAssigneeAndTeam,
    ]
  );

  return (
    <div className="mb-3">
      <GlobalFilter
        listingFilter={listingFilter}
        handleFilterChange={handleFilterChange}
        handleFilterReset={handleFilterReset}
        applyFilters={applyFilters}
        fields={fields}
        columns={3}
        containerClassName="bg-white border rounded-lg"
        defaultExpanded={false}
        expandOnApply={true}
        liveMode={liveMode}
      />
    </div>
  );
}
